import { Alert } from '@cmsgov/design-system';

import { RemoveIcon } from '../Icon';

import './DismissibleAlert.css';

export type DismissibleAlertVariation = 'error' | 'informational' | 'warn' | 'success';

export interface DismissibleAlertProps {
  /**
   * The alert variation/severity
   */
  variation: DismissibleAlertVariation;
  /**
   * The heading text for the alert
   */
  heading: string;
  /**
   * The body text for the alert
   */
  children: React.ReactNode;
  /**
   * Callback when the dismiss button is clicked
   */
  onDismiss: () => void;
  /**
   * Optional additional class name
   */
  className?: string;
  /**
   * Test ID for automated testing
   */
  'data-testid'?: string;
}

/**
 * A dismissible alert component that wraps the CMS Design System Alert
 * with a close button (X icon) in the top-right corner, level with the heading.
 * Centring it instead put the control in the middle of the text on any alert
 * taller than one line.
 *
 * @example
 * <DismissibleAlert
 *   variation="error"
 *   heading="Upload Error"
 *   onDismiss={() => setShowAlert(false)}
 * >
 *   There was an error uploading your file. Please try again.
 * </DismissibleAlert>
 */
export function DismissibleAlert({
  variation,
  heading,
  children,
  onDismiss,
  className = '',
  'data-testid': dataTestId,
}: DismissibleAlertProps) {
  const baseTestId = dataTestId ?? 'dismissible-alert';
  const alertVariation = variation === 'informational' ? undefined : variation;

  return (
    <div
      className={`dismissible-alert ${className}`.trim()}
      data-testid={baseTestId}>
      <Alert
        aria-atomic='true'
        aria-live={variation === 'error' ? 'assertive' : 'polite'}
        className='dismissible-alert__alert'
        data-testid={`${baseTestId}-alert`}
        heading={heading}
        role={variation === 'error' ? 'alert' : 'status'}
        variation={alertVariation}>
        <div
          className='dismissible-alert__content'
          data-testid={`${baseTestId}-content`}>
          {children}
        </div>
      </Alert>
      <button
        aria-label='Dismiss alert'
        className='dismissible-alert__close-button'
        data-testid={`${baseTestId}-dismiss`}
        type='button'
        onClick={onDismiss}>
        <RemoveIcon
          ariaLabel='Close'
          className='dismissible-alert__close-icon'
          size={16}
        />
      </button>
    </div>
  );
}

export default DismissibleAlert;
